import { useTranslate } from '@refinedev/core';
import {
  Card,
  Col,
  Descriptions,
  Flex,
  Grid,
  Image,
  Row,
  Typography,
  theme,
} from 'antd';
import type { DescriptionsProps } from 'antd';
import type { PersonDetailOut } from '@/api/generated/schemas';
import { orDash, useVerticalDescProps } from '@/components/common/show/shared';
import { CollectionFilterShow } from '@/components/query-builder';

type Props = {
  record: PersonDetailOut;
  ns: string;
};

export const PersonCollectionTab = ({ record, ns }: Props) => {
  const t = useTranslate();
  const verticalDescProps = useVerticalDescProps();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  /** Enum values carry their own label namespace per person resource. */
  const enumLabel = (group: string, value?: string | null) =>
    value ? t(`${ns}.${group}.${value}`) : null;

  const items: DescriptionsProps['items'] = [
    {
      key: 'collection_kind',
      label: t(`${ns}.fields.collection_kind`),
      children: orDash(enumLabel('kind', record.collection_kind)),
    },
    {
      key: 'collection_data',
      label: t(`${ns}.fields.collection_data`),
      children: orDash(enumLabel('data', record.collection_data)),
    },
    {
      key: 'header',
      label: t(`${ns}.fields.header`),
      children: orDash(record.header),
    },
  ];

  const images: { key: string; label: string; url?: string | null }[] = [
    {
      key: 'cover_url',
      label: t(`${ns}.fields.cover_url`),
      url: record.cover_url,
    },
    {
      key: 'banner_url',
      label: t(`${ns}.fields.banner_url`),
      url: record.banner_url,
    },
  ];

  return (
    <Row gutter={[token.margin, token.margin]}>
      <Col flex="1 1 480px">
        <Flex vertical gap={token.margin}>
          <Card title={t('base.sections.collection_info')}>
            <Descriptions
              {...verticalDescProps}
              column={{ xs: 1, sm: 2, md: 3 }}
              items={items}
            />
            <CollectionFilterShow
              collectionData={record.collection_data}
              sorting={record.publication_custom_sorting}
            />
          </Card>
          <Card title={t(`${ns}.fields.content`)}>
            <Typography.Paragraph style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
              {orDash(record.content)}
            </Typography.Paragraph>
          </Card>
        </Flex>
      </Col>
      <Col flex={screens.lg ? '0 1 440px' : '0 1 300px'}>
        <Flex vertical gap={token.margin}>
          {images.map((image) => (
            <Card key={image.key} title={image.label}>
              {image.url ? (
                <Image
                  src={image.url}
                  width="100%"
                  style={{
                    objectFit: 'cover',
                    borderRadius: token.borderRadius,
                  }}
                />
              ) : (
                orDash(null)
              )}
            </Card>
          ))}
        </Flex>
      </Col>
    </Row>
  );
};
